import type { WebsiteCardTone } from '../../theme/tokens';

/**
 * The Website hub's card contract. Each card's pure `*-summary.ts` produces a {@link WebsiteCardSummary} whose copy
 * is an id (`C`), never a string: only literal `t()` calls are harvested by the i18n extractor, so the card's hook
 * maps every id to its literal key in a `*-copy.ts` switch and resolves the summary with {@link resolveSummary} into
 * the {@link WebsiteCardView} that `WebsiteHubCard` renders.
 */

export type { WebsiteCardTone } from '../../theme/tokens';

/** Optional artwork beside a context line: a thumbnail and colour swatches (the card shows up to six). */
export type WebsiteCardArt = { thumbUrl?: string; swatches?: string[] };

/** What the card body says. Exactly one kind at a time. */
export type WebsiteCardBody<C> =
  | { kind: 'loading' }
  | { kind: 'failed' }
  /** One or two lines, each naming one thing that waits for the owner. */
  | { kind: 'waiting'; lines: C[] }
  /** One line of useful context, when nothing waits. */
  | { kind: 'context'; line: C; art?: WebsiteCardArt }
  /** The area has nothing in it yet. */
  | { kind: 'get-started'; line: C };

/** An action: navigate to `to`, or run the hook's handler named `run`. */
export type WebsiteCardIntent<C> = { copy: C; to: string } | { copy: C; run: string };

export type WebsiteCardSummary<C> = {
  chip: { tone: WebsiteCardTone; copy: C } | null;
  body: WebsiteCardBody<C>;
  primary: WebsiteCardIntent<C> | null;
  secondary: WebsiteCardIntent<C> | null;
};

/** A resolved action: a translated label, and where it goes or what it runs. */
export type WebsiteCardAction = { label: string; to?: string; onActivate?: () => void };

export type WebsiteCardViewBody =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'waiting'; lines: string[] }
  | { kind: 'context'; line: string; art?: WebsiteCardArt }
  | { kind: 'get-started'; line: string };

/** What `WebsiteHubCard` renders: translated strings and handlers only. */
export type WebsiteCardView = {
  chip: { tone: WebsiteCardTone; label: string } | null;
  body: WebsiteCardViewBody;
  primary: WebsiteCardAction | null;
  secondary: WebsiteCardAction | null;
};

/** When several statuses apply, the card shows the first of these. */
export const TONE_PRIORITY: readonly WebsiteCardTone[] = ['problem', 'waiting', 'working', 'neutral'];

/** The one chip to show: the highest-priority candidate (the first of equals), or null when none applies. */
export const pickChip = <C>(
  candidates: Array<{ tone: WebsiteCardTone; copy: C } | null>,
): { tone: WebsiteCardTone; copy: C } | null => {
  let best: { tone: WebsiteCardTone; copy: C } | null = null;
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (!best || TONE_PRIORITY.indexOf(candidate.tone) < TONE_PRIORITY.indexOf(best.tone)) best = candidate;
  }
  return best;
};

/** The view every card shows while its reads are in flight: a skeleton line and its default action only. */
export const loadingView = (primary: WebsiteCardAction): WebsiteCardView => ({
  chip: null,
  body: { kind: 'loading' },
  primary,
  secondary: null,
});

/**
 * The view of a card that cannot address its brand: loading until the brand itself is in, then, for a brand loaded
 * without the ids the card reads by (one still being created), the empty body with its default action, never a
 * skeleton that never ends.
 */
export const unaddressableView = (brandLoaded: boolean, primary: WebsiteCardAction): WebsiteCardView =>
  brandLoaded ? { chip: null, body: { kind: 'failed' }, primary, secondary: null } : loadingView(primary);

const resolveBody = <C>(body: WebsiteCardBody<C>, copy: (c: C) => string): WebsiteCardViewBody => {
  switch (body.kind) {
    case 'loading':
    case 'failed':
      return { kind: body.kind };
    case 'waiting':
      return { kind: 'waiting', lines: body.lines.map(copy) };
    case 'context':
      return body.art
        ? { kind: 'context', line: copy(body.line), art: body.art }
        : { kind: 'context', line: copy(body.line) };
    case 'get-started':
      return { kind: 'get-started', line: copy(body.line) };
  }
};

const resolveIntent = <C>(
  intent: WebsiteCardIntent<C> | null,
  copy: (c: C) => string,
  run: (name: string) => (() => void) | undefined,
): WebsiteCardAction | null => {
  if (!intent) return null;
  if ('to' in intent) return { label: copy(intent.copy), to: intent.to };
  const handler = run(intent.run);
  return handler ? { label: copy(intent.copy), onActivate: handler } : null;
};

/**
 * A summary as the card renders it: every copy id through `copy`, every `run` intent wired to the handler `run`
 * returns for its name. An intent whose handler is missing resolves to null, so the card never shows a button that
 * does nothing.
 */
export const resolveSummary = <C>(
  summary: WebsiteCardSummary<C>,
  copy: (c: C) => string,
  run: (name: string) => (() => void) | undefined,
): WebsiteCardView => ({
  chip: summary.chip ? { tone: summary.chip.tone, label: copy(summary.chip.copy) } : null,
  body: resolveBody(summary.body, copy),
  primary: resolveIntent(summary.primary, copy, run),
  secondary: resolveIntent(summary.secondary, copy, run),
});
